import { useEffect } from 'react';
import { SITE_NAME, SITE_URL } from '../config';

type PageMeta = {
  /** Page title without the site suffix; the hook appends " | Peacock Omlettes". */
  title: string;
  description: string;
  /** Path beginning with '/', used for the canonical URL. */
  path: string;
  /** Optional JSON-LD object injected for this page and removed on unmount. */
  jsonLd?: object;
};

/**
 * Per-route document metadata for the SPA. CloudFront serves one index.html,
 * so route-level title/description/canonical/JSON-LD are applied client-side.
 * Google renders JS for indexing; social scrapers will see the site-level
 * tags from index.html, which is acceptable for this site's goals.
 */
export function usePageMeta({ title, description, path, jsonLd }: PageMeta): void {
  // Serialize for the dependency array so callers can pass fresh object
  // literals each render without re-running the effect.
  const ldJson = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    document.title = `${title} | ${SITE_NAME}`;

    let desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.name = 'description';
      document.head.appendChild(desc);
    }
    desc.content = description;

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const hadCanonical = !!canonical;
    if (canonical) {
      // Staging builds strip the canonical tag; never re-introduce one there.
      canonical.href = `${SITE_URL}${path === '/' ? '/' : path}`;
    }

    let ldScript: HTMLScriptElement | null = null;
    if (ldJson) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.dataset.pageLd = 'true';
      ldScript.text = ldJson;
      document.head.appendChild(ldScript);
    }

    return () => {
      if (ldScript) ldScript.remove();
      if (hadCanonical && canonical) canonical.href = `${SITE_URL}/`;
    };
  }, [title, description, path, ldJson]);
}
